import React from "react";

import { type CrudFilters, useNavigation, useParsed } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  CircleDollarSign,
  Files,
  ListChecks,
  Radio,
  ShieldQuestion,
  SkipForward,
  X,
  XCircle,
} from "lucide-react";

import { ColoredBadge } from "@/components/common/colored-badge";
import { CopyButton } from "@/components/common/copy-button";
import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { makeTooltip } from "@/components/ui/tooltip";
import { asStringArray } from "@/lib/tina/value-readers";
import { cn } from "@/lib/utils";
import { RunStatusBadge } from "@/pages/tina/components/run-status-badge";
import { useTinaPoll } from "@/pages/tina/hooks/use-tina-poll";
import { useItemsColumns } from "@/pages/tina/items/hooks/use-items-columns";
import {
  ENTITY_LABELS,
  ENTITY_ORDER,
  entityHref,
  labelFromKey,
} from "@/pages/tina/items/lib/entity-labels";
import { formatUsdCost } from "@/pages/tina/lib/format-cost";
import { CostBreakdownDialog } from "@/pages/tina/runs/components/cost-breakdown-dialog";
import { EnrichmentEntitiesPanel } from "@/pages/tina/runs/components/enrichment-entities-panel";
import {
  EnrichmentRunBadge,
  EnrichmentRunMeta,
} from "@/pages/tina/runs/components/enrichment-run-meta";
import { EnrichmentRunMetrics } from "@/pages/tina/runs/components/enrichment-run-metrics";
import { MetricCard, shareOf } from "@/pages/tina/runs/components/metric-card";
import { RefreshJobsTable } from "@/pages/tina/runs/components/refresh-jobs-table";
import {
  RefreshRunBadge,
  RefreshRunMeta,
} from "@/pages/tina/runs/components/refresh-run-meta";
import { RefreshRunMetrics } from "@/pages/tina/runs/components/refresh-run-metrics";
import { useEnrichmentRunFacts } from "@/pages/tina/runs/hooks/use-enrichment-run-facts";
import { useRefreshRunSummary } from "@/pages/tina/runs/hooks/use-refresh-run-summary";
import { useRunsRepository } from "@/pages/tina/runs/hooks/use-runs-repository";
import { inFlightCount } from "@/pages/tina/runs/lib/refresh-jobs-filters";
import {
  enrichmentRunSpec,
  refreshRunHourLabel,
  runJobKind,
} from "@/pages/tina/runs/runs-filters";
import type {
  ItemStatus,
  PipelineItem,
  RefreshOutcome,
  RunDetail,
  RunSummaryRow,
} from "@/types/tina";

// The metric blocks count by `final_outcome`, but the run-scoped /events
// endpoint filters by `status` — map each block to its closest status so a
// click narrows the table to (approximately) the items that block counts.
const BLOCK_STATUS_LABEL: Partial<Record<ItemStatus, string>> = {
  done: "Created",
  hitl: "HITL",
  duplicate: "Duplicate",
  skipped: "Skipped",
  failed: "Failed",
};

/** The same statuses, named the way an enrichment tile names them. */
const ENRICHMENT_BLOCK_STATUS_LABEL: Partial<Record<ItemStatus, string>> = {
  done: "Enriched",
  hitl: "Parked",
  skipped: "Skipped",
  failed: "Failed",
};

const isRunLive = (d?: RunDetail) =>
  !d || d.summary.status === "queued" || d.summary.status === "running";

const TRIGGER_COLORS: Record<string, string> = {
  manual: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  cron: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
};

const formatDuration = (
  started: string | null,
  finished: string | null,
): string => {
  if (!started) return "—";
  const end = finished ? new Date(finished) : new Date();
  const ms = end.getTime() - new Date(started).getTime();
  if (ms < 0) return "—";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
};

const LivePill = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-blue-500/20 dark:text-blue-300">
    <span className="relative inline-flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75 animate-ping" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
    </span>
    Live
  </span>
);

interface RunEntityEntry {
  key: string;
  label: string;
  id: string;
  itemId: string;
  site: string;
  contentType: string;
}

interface RunEntityGroup {
  key: string;
  label: string;
  entries: RunEntityEntry[];
}

const ShowSkeleton = () => (
  <div className="flex flex-col gap-4 p-6">
    <Card>
      <CardContent className="p-5 flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <Skeleton className="h-4 w-3/5" />
      </CardContent>
    </Card>
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={i} className="h-[88px] rounded-lg" />
      ))}
    </div>
    <Skeleton className="h-[420px] rounded-lg" />
  </div>
);

interface ProgressStripProps {
  /** Items that reached a verdict. Each run class defines its own set. */
  processed: number;
  total: number;
}

const ProgressStrip = ({ processed, total }: ProgressStripProps) => {
  const pct = total > 0 ? Math.min(100, (processed / total) * 100) : 0;

  return (
    <div className="flex items-center gap-3">
      <Progress value={pct} className="h-1.5 flex-1" />
      <span className="text-[11px] font-mono tabular-nums text-muted-foreground whitespace-nowrap">
        {processed} / {total} · {pct.toFixed(0)}%
      </span>
    </div>
  );
};

/**
 * The scrape outcome set. `created`/`duplicate` are scrape-only, so summing the
 * same five for an enrichment run reported 0 of 14 done — see
 * {@link collectEnrichmentRunFacts}.
 */
const scrapeProcessed = (summary: RunSummaryRow): number =>
  summary.created_count +
  summary.hitl_count +
  summary.duplicate_count +
  summary.skipped_count +
  summary.failed_count;

export const RunsShow = () => {
  const { id } = useParsed();
  const idStr = String(id ?? "");
  const repo = useRunsRepository();
  const { show } = useNavigation();
  const [costDialogOpen, setCostDialogOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<ItemStatus | null>(
    null,
  );
  const toggleStatus = React.useCallback(
    (status: ItemStatus) =>
      setStatusFilter((prev) => (prev === status ? null : status)),
    [],
  );
  const tableFilters: CrudFilters = statusFilter
    ? [{ field: "status", operator: "eq", value: statusFilter }]
    : [];

  const detailQuery = useTinaPoll<RunDetail>(
    ["tina", "runs", idStr],
    () => repo.get(idStr),
    {
      enabled: !!idStr,
      shouldPoll: isRunLive,
    },
  );
  const { data: detail, dataUpdatedAt } = detailQuery;
  // Read off `trigger_spec`, so it settles to `enrichment` once the detail
  // query resolves and stays there. Everything class-specific on this page
  // hangs off this one flag.
  const isEnrichment = runJobKind(detail?.run.trigger_spec) === "enrichment";
  // The third class. `trigger_type` is `"cron"` on a refresh run, so the spec
  // is the only discriminator — same reason `isEnrichment` reads it.
  const isRefresh = runJobKind(detail?.run.trigger_spec) === "refresh";
  // Every row of an enrichment run is a job row, so `site` (always
  // `system-enrichment`), `url` (always null) and `context` are three empty
  // columns; show the job axes instead.
  const itemColumns = useItemsColumns({
    variant: isEnrichment ? "enrichment" : "default",
  });
  const isLive = isRunLive(detail);
  const isRunFinalised =
    detail?.summary.status === "done" || detail?.summary.status === "cancelled";

  const { facts: enrichmentFacts, isLoading: isLoadingEnrichmentFacts } =
    useEnrichmentRunFacts(idStr, {
      enabled: Boolean(detail) && isEnrichment,
      isLive,
    });

  // The refresh run's own outcome slice, which is a route param
  // (`refresh_outcome`) rather than the `status` approximation the other two
  // classes' tiles fall back to — so a tile click narrows the table to exactly
  // the rows the tile counted.
  const [outcomeFilter, setOutcomeFilter] =
    React.useState<RefreshOutcome | null>(null);
  const toggleOutcome = React.useCallback(
    (outcome: RefreshOutcome) =>
      setOutcomeFilter((prev) => (prev === outcome ? null : outcome)),
    [],
  );
  const clearOutcome = React.useCallback(() => setOutcomeFilter(null), []);
  const refreshFilterState = React.useMemo(
    () => ({
      runId: idStr,
      outcome: outcomeFilter,
      mode: null,
      status: null,
      site: null,
    }),
    [idStr, outcomeFilter],
  );
  const { summary: refreshSummary } = useRefreshRunSummary(refreshFilterState, {
    enabled: Boolean(detail) && isRefresh,
    isLive,
  });

  const { data: allItemsForEntities, isLoading: isLoadingCreatedEntities } =
    useQuery({
      queryKey: ["tina", "runs", idStr, "all-created-entities"],
      queryFn: () => repo.listAllItems(idStr),
      // Scrape only: an enrichment run's entities come from
      // `useEnrichmentRunFacts`, and letting both run would fetch the same
      // rows twice. A refresh run creates no entities at all — it PATCHes
      // events that already exist — and paging its whole thousands-of-jobs
      // batch to prove that would be the page's most expensive request.
      enabled:
        !!idStr && !!detail && isRunFinalised && !isEnrichment && !isRefresh,
      staleTime: 30_000,
    });

  const createdEntityGroups = React.useMemo(
    () => collectCreatedEntityGroups(allItemsForEntities ?? []),
    [allItemsForEntities],
  );

  const table = useTable<PipelineItem>({
    columns: itemColumns,
    refineCoreProps: {
      resource: "tina-runs",
      meta: { urlPath: `runs/${idStr}/events` },
      pagination: { pageSize: 50 },
      filters: { permanent: tableFilters },
      // A refresh run renders `RefreshJobsTable` instead, off
      // `GET /refresh-jobs`. The hook still has to be called — hooks are not
      // conditional — but its request must not be, or every refresh run page
      // would fetch a page of rows nothing renders.
      queryOptions: { enabled: !!idStr && !isRefresh },
    },
    initialState: {
      sorting: [{ id: "created_at", desc: false }],
      columnPinning: {
        right: ["actions"],
      },
    },
    meta: {
      onDoubleClick: (
        _e: React.MouseEvent,
        row: { original: PipelineItem },
      ) => {
        show("tina-items", row.original.id);
      },
    },
  });

  const refetchTable = table.refineCore.tableQuery.refetch;
  const setCurrentPage = table.refineCore.setCurrentPage;

  // Reset to the first page whenever the status filter changes so a click never
  // strands the table on an out-of-range page for the smaller filtered set.
  const prevStatusFilterRef = React.useRef(statusFilter);
  React.useEffect(() => {
    if (prevStatusFilterRef.current === statusFilter) return;
    prevStatusFilterRef.current = statusFilter;
    setCurrentPage(1);
  }, [statusFilter, setCurrentPage]);

  const terminateMutation = useMutation({
    mutationFn: () => repo.terminate(idStr),
    onSuccess: () => {
      detailQuery.refetch();
      refetchTable();
    },
  });

  const eventCount = detail?.summary.event_count;
  const lastEventCountRef = React.useRef<number | undefined>(eventCount);
  React.useEffect(() => {
    console.debug(isLive, dataUpdatedAt, lastEventCountRef, eventCount);
    if (!isLive || dataUpdatedAt === 0) return;
    // if (lastEventCountRef.current === eventCount) return;
    lastEventCountRef.current = eventCount;
    refetchTable();
  }, [dataUpdatedAt, isLive, eventCount, refetchTable]);

  const createdAt = React.useMemo(
    () => (detail ? dayjs(detail.run.created_at) : null),
    [detail?.run.created_at],
  );

  if (!detail || !createdAt) return <ShowSkeleton />;

  const { run, summary } = detail;
  // `v_run_summary.event_count` already excludes the carry-site discover seeds
  // (no parent, `current_stage='discover'`, null url), which is the same
  // exclusion `v_refresh_jobs` makes — but the summary is the authority on a
  // refresh run's own denominator, and it is what the tiles divide by.
  const total = isRefresh
    ? (refreshSummary?.total ?? summary.event_count)
    : summary.event_count;
  const duration = formatDuration(run.started_at, run.finished_at);
  const totalRows = table.refineCore.tableQuery.data?.total;
  const runSpec = enrichmentRunSpec(run.trigger_spec);
  const refreshHour = refreshRunHourLabel(run.trigger_spec);
  const processed = isEnrichment
    ? (enrichmentFacts?.settled ?? 0)
    : isRefresh
      ? // Every job the summary counted, minus the NULL bucket. Not
        // `scrapeProcessed`, whose five counters see only two refresh outcomes
        // — `hitl_count` and `failed_count` — so the strip read 0 / 812 for a
        // run that had finished everything.
        refreshSummary
        ? refreshSummary.total - inFlightCount(refreshSummary.outcomes)
        : 0
      : scrapeProcessed(summary);

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Header */}
      <Card className="overflow-hidden">
        <div className="flex">
          <div
            className={cn(
              "w-1 self-stretch",
              run.status === "running" && "bg-blue-500",
              run.status === "queued" && "bg-zinc-400",
              run.status === "done" && "bg-emerald-500",
              run.status === "failed" && "bg-red-500",
              run.status === "cancelled" && "bg-zinc-500",
            )}
            aria-hidden
          />
          <CardContent className="p-5 flex flex-col gap-3 flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  Run
                </span>
                {makeTooltip(
                  <h1 className="text-2xl font-semibold font-mono tracking-tight">
                    {run.id.slice(0, 8)}
                  </h1>,
                  run.id,
                )}
                <CopyButton text={run.id} message="Run ID copied" />
              </div>
              <RunStatusBadge status={run.status} />
              {/* `trigger_type` is `manual` on every enrichment run and `cron`
                  on every refresh run, so for both the badge would say the one
                  thing that never varies. */}
              {runSpec ? (
                <EnrichmentRunBadge />
              ) : isRefresh ? (
                <RefreshRunBadge hour={refreshHour} />
              ) : (
                <ColoredBadge
                  getValue={() => run.trigger_type}
                  colors={TRIGGER_COLORS}
                />
              )}
              {isLive && <LivePill />}
              {isLive && (
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  onClick={() => terminateMutation.mutate()}
                  disabled={terminateMutation.isPending}
                  className="h-7 gap-1.5 px-2 text-xs"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  {terminateMutation.isPending
                    ? "Terminating"
                    : "Terminate run"}
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
              {makeTooltip(
                <span className="font-mono tabular-nums">
                  {createdAt.format("DD/MM/YYYY HH:mm")}
                </span>,
                createdAt.format("ddd, MMM D, YYYY HH:mm:ss"),
              )}
              <Separator orientation="vertical" className="h-3" />
              <span className="inline-flex items-center gap-1">
                <Activity className="h-3 w-3" />
                <span className="font-mono tabular-nums">{duration}</span>
              </span>
              {run.started_at && (
                <>
                  <Separator orientation="vertical" className="h-3" />
                  <span>
                    started{" "}
                    <span className="font-mono tabular-nums">
                      {dayjs(run.started_at).format("HH:mm:ss")}
                    </span>
                  </span>
                </>
              )}
              {run.finished_at && (
                <>
                  <Separator orientation="vertical" className="h-3" />
                  <span>
                    finished{" "}
                    <span className="font-mono tabular-nums">
                      {dayjs(run.finished_at).format("HH:mm:ss")}
                    </span>
                  </span>
                </>
              )}
            </div>

            {runSpec && <EnrichmentRunMeta spec={runSpec} />}
            {isRefresh && <RefreshRunMeta hour={refreshHour} />}
          </CardContent>
        </div>
      </Card>

      {/* Progress strip */}
      {total > 0 && (
        <Card>
          <CardContent className="p-3">
            <ProgressStrip processed={processed} total={total} />
          </CardContent>
        </Card>
      )}

      {/* Metric grid */}
      {isRefresh ? (
        <RefreshRunMetrics
          summary={refreshSummary}
          outcomeFilter={outcomeFilter}
          onToggleOutcome={toggleOutcome}
          onClearOutcome={clearOutcome}
          onCostClick={() => setCostDialogOpen(true)}
        />
      ) : isEnrichment ? (
        <EnrichmentRunMetrics
          facts={enrichmentFacts}
          summary={summary}
          statusFilter={statusFilter}
          onToggleStatus={toggleStatus}
          onCostClick={() => setCostDialogOpen(true)}
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          <MetricCard
            label="Items"
            value={summary.event_count}
            icon={<ListChecks className="h-3.5 w-3.5" />}
            tone="sky"
            hint={`${summary.root_count} roots · ${summary.child_count} children`}
          />
          <MetricCard
            label="Created"
            value={summary.created_count}
            icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            tone="emerald"
            share={shareOf(summary.created_count, total)}
            onClick={() => toggleStatus("done")}
            active={statusFilter === "done"}
          />
          <MetricCard
            label="HITL"
            value={summary.hitl_count}
            icon={<ShieldQuestion className="h-3.5 w-3.5" />}
            tone="orange"
            share={shareOf(summary.hitl_count, total)}
            onClick={() => toggleStatus("hitl")}
            active={statusFilter === "hitl"}
          />
          <MetricCard
            label="Duplicate"
            value={summary.duplicate_count}
            icon={<Files className="h-3.5 w-3.5" />}
            tone="zinc"
            share={shareOf(summary.duplicate_count, total)}
            onClick={() => toggleStatus("duplicate")}
            active={statusFilter === "duplicate"}
          />
          <MetricCard
            label="Skipped"
            value={summary.skipped_count}
            icon={<SkipForward className="h-3.5 w-3.5" />}
            tone="zinc"
            share={shareOf(summary.skipped_count, total)}
            onClick={() => toggleStatus("skipped")}
            active={statusFilter === "skipped"}
          />
          <MetricCard
            label="Failed"
            value={summary.failed_count}
            icon={
              summary.failed_count > 0 ? (
                <XCircle className="h-3.5 w-3.5" />
              ) : (
                <AlertTriangle className="h-3.5 w-3.5" />
              )
            }
            tone="red"
            share={shareOf(summary.failed_count, total)}
            hint={
              summary.discover_seed_failed_count > 0
                ? `${summary.discover_seed_failed_count} discover seeds`
                : undefined
            }
            onClick={() => toggleStatus("failed")}
            active={statusFilter === "failed"}
          />
          <MetricCard
            label="Cost"
            value={
              <span
                className={cn(!summary.total_cost && "text-muted-foreground")}
              >
                {formatUsdCost(summary.total_cost)}
              </span>
            }
            icon={<CircleDollarSign className="h-3.5 w-3.5" />}
            tone="indigo"
            hint="click for breakdown"
            onClick={() => setCostDialogOpen(true)}
          />
        </div>
      )}

      <CostBreakdownDialog
        runId={idStr}
        open={costDialogOpen}
        onOpenChange={setCostDialogOpen}
      />

      {isRefresh ? (
        <RefreshJobsTable
          filterState={refreshFilterState}
          onClearOutcome={clearOutcome}
          // The detail poll is this page's clock; reuse its tick so a live
          // run's job table follows the counters instead of going stale under
          // them.
          refetchToken={dataUpdatedAt}
        />
      ) : (
        <PipelineItemsTable
          table={table}
          totalRows={totalRows}
          statusFilter={statusFilter}
          onClearFilter={() => setStatusFilter(null)}
          isEnrichment={isEnrichment}
        />
      )}

      {isEnrichment ? (
        // Always rendered, not gated on a finalised run: the entity list is the
        // point of the page here, and the rows it reads are already fetched.
        <EnrichmentEntitiesPanel
          facts={enrichmentFacts}
          isLoading={isLoadingEnrichmentFacts}
        />
      ) : (
        // A refresh run creates nothing — it PATCHes events that already
        // exist — so there is no entity list to render and no rows fetched to
        // build one from.
        !isRefresh &&
        (summary.status === "done" || summary.status === "cancelled") && (
          <CreatedEntitiesSummary
            groups={createdEntityGroups}
            isLoading={isLoadingCreatedEntities}
          />
        )
      )}
    </div>
  );
};

function PipelineItemsTable({
  table,
  totalRows,
  statusFilter,
  onClearFilter,
  isEnrichment,
}: {
  table: ReturnType<typeof useTable<PipelineItem>>;
  totalRows: number | undefined;
  statusFilter: ItemStatus | null;
  onClearFilter: () => void;
  isEnrichment: boolean;
}) {
  const labels = isEnrichment
    ? ENRICHMENT_BLOCK_STATUS_LABEL
    : BLOCK_STATUS_LABEL;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Radio className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-sm font-semibold">
            {isEnrichment ? "Enrichment jobs" : "Pipeline items"}
          </h2>
          {typeof totalRows === "number" && (
            <span className="text-[11px] font-mono tabular-nums text-muted-foreground">
              {totalRows}
            </span>
          )}
          {statusFilter && (
            <button
              type="button"
              onClick={onClearFilter}
              className="inline-flex items-center gap-1 rounded-full border bg-accent/60 px-2 py-0.5 text-[11px] font-medium hover:bg-accent"
            >
              {labels[statusFilter] ?? statusFilter}
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground">
          double-click a row to open
        </span>
      </div>
      <div className="p-3">
        <DataTable table={table} />
      </div>
    </Card>
  );
}

function CreatedEntitiesSummary({
  groups,
  isLoading,
}: {
  groups: RunEntityGroup[];
  isLoading: boolean;
}) {
  const total = groups.reduce((sum, group) => sum + group.entries.length, 0);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Files className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Created entities</h2>
          <Badge variant="secondary">{total}</Badge>
        </div>
        <span className="text-[11px] text-muted-foreground">
          deduped from every done item in this run
        </span>
      </div>
      <CardContent className="p-3">
        {isLoading ? (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-28 rounded-md" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
            No created entities recorded for this run.
          </div>
        ) : (
          <ScrollArea className="h-[360px]">
            <div className="grid gap-3 pr-3 xl:grid-cols-2 2xl:grid-cols-3">
              {groups.map((group) => (
                <div key={group.key} className="rounded-md border bg-card">
                  <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground">
                      {group.label}
                    </h3>
                    <Badge variant="secondary">{group.entries.length}</Badge>
                  </div>
                  <div className="divide-y">
                    {group.entries.map((entry) => (
                      <CreatedEntityRow
                        key={`${entry.key}:${entry.id}:${entry.itemId}`}
                        entry={entry}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

function CreatedEntityRow({ entry }: { entry: RunEntityEntry }) {
  const href = entityHref(entry.key, entry.id);
  const content = (
    <>
      <div className="min-w-0">
        <p className="truncate font-mono text-xs font-medium">{entry.id}</p>
        <p className="truncate text-[11px] text-muted-foreground">
          item {entry.itemId.slice(0, 8)} · {entry.site} · {entry.contentType}
        </p>
      </div>
      {href && (
        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </>
  );

  if (!href) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        {content}
      </div>
    );
  }

  return (
    <a
      href={href}
      className="group flex items-center justify-between gap-2 px-3 py-2 hover:bg-accent"
    >
      {content}
    </a>
  );
}

function collectCreatedEntityGroups(items: PipelineItem[]): RunEntityGroup[] {
  const groups = new Map<string, RunEntityEntry[]>();
  const seen = new Set<string>();

  for (const item of items) {
    const entities = item.created_entities ?? {};
    const entries = Object.entries(entities);

    if (item.created_event_id && !Object.hasOwn(entities, "event_id")) {
      entries.push(["event_id", item.created_event_id]);
    }

    for (const [key, value] of entries) {
      for (const id of asStringArray(value)) {
        const seenKey = `${key}:${id}`;
        if (seen.has(seenKey)) continue;
        seen.add(seenKey);

        let arr = groups.get(key);
        if (!arr) groups.set(key, (arr = []));
        arr.push({
          key,
          label: ENTITY_LABELS[key] ?? labelFromKey(key),
          id,
          itemId: item.id,
          site: item.site,
          contentType: item.content_type,
        });
      }
    }
  }

  return Array.from(groups.entries())
    .map(([key, entries]) => ({
      key,
      label: ENTITY_LABELS[key] ?? labelFromKey(key),
      entries: entries.sort((left, right) => left.id.localeCompare(right.id)),
    }))
    .sort(
      (left, right) => entitySortValue(left.key) - entitySortValue(right.key),
    );
}

function entitySortValue(key: string) {
  const index = ENTITY_ORDER.indexOf(key);
  return index === -1 ? ENTITY_ORDER.length : index;
}
